import { useCallback, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import APIHandler from 'config/APIHandler';

import type { DirectoryConfig, LatLng } from 'hooks/useDirectoryConfig/types';
import { useSettings } from 'hooks/useSettings';

import { fetchFreeListings, fetchPremiumListings } from './fetchListings';
import type { Category, CustomFilterConfig, FreeHotelItem, PremiumHotelItem, Subcategory } from './types';

interface UseDirectoryDataReturn {
  premiumHotelsList: PremiumHotelItem[];
  freeHotelsList: FreeHotelItem[];
  categories: Category[];
  customFilters?: CustomFilterConfig[];
  loading: boolean;
  selectedCategory: Category | null;
  selectedSubcategory: Subcategory | null;
  query: string;
  zoom: number;
  center: LatLng;
  language: string;
  lat1: number;
  lat2: number;
  lon1: number;
  lon2: number;
  setQuery: (query: string) => void;
  setSelectedCategory: (category: Category | null) => void;
  setSelectedSubcategory: (subcategory: Subcategory | null) => void;
  setZoom: (zoom: number) => void;
  setCenter: (center: LatLng) => void;
  setMapVisible: (visible: boolean) => void;
  updateBounds: (lat1: number, lat2: number, lon1: number, lon2: number, zoom: number) => void;
  resetMap: () => void;
  resetFilters: () => void;
}

function getCategoryParams(category: Category | null, subcategory: Subcategory | null) {
  return {
    categoryParam: category ? `&category=${encodeURIComponent(category.cat_name)}` : '',
    subcategoryParam: subcategory ? `&subcategory=${encodeURIComponent(subcategory.sub_catname)}` : '',
  };
}

interface ListingsCategoriesResponse {
  categoryFilter: Category[];
  customFilters?: CustomFilterConfig[];
}

function isListingsCategoriesResponse(value: unknown): value is ListingsCategoriesResponse {
  if (value === null || typeof value !== 'object' || !('categoryFilter' in value)) return false;
  return Array.isArray((value as ListingsCategoriesResponse).categoryFilter);
}

const IS_DEV = import.meta.env.DEV;

const loadMockListingsCategories = async (appId: string): Promise<unknown> => {
  try {
    const module = (await import(`./devOverrides/category-filter-${appId}.json`)) as {
      default: unknown;
    };
    return module.default;
  } catch {
    const fallback = (await import('./devOverrides/category-filter-default.json')) as {
      default: unknown;
    };
    return fallback.default;
  }
};

function getCategoriesQueryKey(appId: string | null, table: string, language: string, enableMock: boolean) {
  return ['directoryCategories', appId, table, language, enableMock] as const;
}

function getCategoriesQueryFn(appId: string | null, table: string, language: string, enableMock: boolean) {
  return async (): Promise<{ categories: Category[]; customFilters?: CustomFilterConfig[] }> => {
    const result: unknown =
      enableMock && IS_DEV
        ? await loadMockListingsCategories(appId ?? '')
        : await APIHandler.getListingsCategories(appId, table, language);
    if (!isListingsCategoriesResponse(result)) return { categories: [] };

    const categories = result.categoryFilter.map((cat) => {
      return {
        ...cat,
        subcategories: cat.subcategories.filter((sub) => {
          return sub.sub_catname.trim() !== '';
        }),
      };
    });
    return { categories, customFilters: result.customFilters };
  };
}

interface HotelsResult {
  freeHotelsList: FreeHotelItem[];
  premiumHotelsList: PremiumHotelItem[];
}

interface HotelsQueryParams {
  appId: string | null;
  config: DirectoryConfig;
  lat1: number;
  lat2: number;
  lon1: number;
  lon2: number;
  query: string;
  category: Category | null;
  subcategory: Subcategory | null;
}

function getHotelsQueryKey({ appId, config, lat1, lat2, lon1, lon2, query, category, subcategory }: HotelsQueryParams) {
  return [
    'directoryHotels',
    appId,
    config.listings.table,
    lat1,
    lat2,
    lon1,
    lon2,
    query,
    category?.cat_name ?? null,
    subcategory?.sub_catname ?? null,
  ] as const;
}

// Takes enableLocation as a resolved boolean rather than reading a ref itself — callers (the inline
// queryFn in useDirectoryData) read mapVisibleRef.current lazily at actual fetch time, never during
// render, since map visibility shouldn't drive the query key (it flips rapidly during window resize).
async function fetchHotels(params: HotelsQueryParams, enableLocation: boolean): Promise<HotelsResult> {
  const { appId, config, lat1, lat2, lon1, lon2, query, category, subcategory } = params;
  const { categoryParam, subcategoryParam } = getCategoryParams(category, subcategory);
  const bounds = { lat1, lat2, lon1, lon2 };

  const [freeHotelsList, premiumHotelsList] = await Promise.all([
    config.listings.enableFreeListings
      ? fetchFreeListings(
          appId ?? '',
          bounds,
          config.listings.table,
          categoryParam,
          subcategoryParam,
          query,
          enableLocation,
        )
      : Promise.resolve<FreeHotelItem[]>([]),
    config.listings.enablePremiumListings
      ? fetchPremiumListings(
          appId ?? '',
          bounds,
          categoryParam,
          subcategoryParam,
          config.listings.live,
          config.listings.preview,
          enableLocation,
        )
      : Promise.resolve<PremiumHotelItem[]>([]),
  ]);

  return { freeHotelsList, premiumHotelsList };
}

export const useDirectoryData = (
  config: DirectoryConfig,
  initialMapVisible = true,
  enableMock = false,
): UseDirectoryDataReturn => {
  // config is guaranteed non-null — callers must wait for useDirectoryConfig to resolve before invoking this hook
  const { appId, lang } = useSettings();
  const language = lang?.langFile ?? 'en';

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory | null>(null);

  // Tracks map visibility for the hotels query to read at fetch time. A ref, not state — it can flip
  // rapidly during window resize, and shouldn't cause a render or a refetch on its own; the map's own
  // onIdle (or updateBounds, called explicitly when the map is hidden) already re-fetches when needed.
  const mapVisibleRef = useRef(initialMapVisible);
  const setMapVisible = useCallback((visible: boolean) => {
    mapVisibleRef.current = visible;
  }, []);

  const [query, setQuery] = useState<string>('');

  const [zoom, setZoom] = useState<number>(config.map.zoom);
  const [center, setCenter] = useState<LatLng>(config.map.center);

  const [lat1, setLat1] = useState<number>(config.map.initialBounds.lat1);
  const [lat2, setLat2] = useState<number>(config.map.initialBounds.lat2);
  const [lon1, setLon1] = useState<number>(config.map.initialBounds.lon1);
  const [lon2, setLon2] = useState<number>(config.map.initialBounds.lon2);

  const categoriesQuery = useQuery({
    queryKey: getCategoriesQueryKey(appId, config.listings.table, language, enableMock),
    queryFn: getCategoriesQueryFn(appId, config.listings.table, language, enableMock),
    enabled: appId !== null,
  });

  const hotelsParams: HotelsQueryParams = {
    appId,
    config,
    lat1,
    lat2,
    lon1,
    lon2,
    query,
    category: selectedCategory,
    subcategory: selectedSubcategory,
  };

  // Bounds/query/category/subcategory are part of the query key, so any setter below (updateBounds,
  // setQuery, setSelectedCategory, ...) automatically triggers a refetch — no manual fetch calls needed.
  // The initial fetch (using config.map.initialBounds) happens on mount for the same reason; the map's
  // onIdle later calls updateBounds with the real viewport bounds, replacing this result.
  //
  // queryFn is defined inline (rather than via a factory call) so mapVisibleRef.current is only read
  // once this callback actually runs — never synchronously during render.
  const hotelsQuery = useQuery({
    queryKey: getHotelsQueryKey(hotelsParams),
    queryFn: async () => {
      const enableLocation = mapVisibleRef.current ? config.listings.location : false;
      return fetchHotels(hotelsParams, enableLocation);
    },
    placeholderData: (previous) => {
      return previous;
    },
    retry: false,
  });

  const updateBounds = useCallback(
    (newLat1: number, newLat2: number, newLon1: number, newLon2: number, newZoom: number) => {
      setLat1(newLat1);
      setLat2(newLat2);
      setLon1(newLon1);
      setLon2(newLon2);
      setZoom(newZoom);
    },
    [],
  );

  const resetMap = useCallback(() => {
    setZoom(config.map.zoom);
    // Spread to always produce a new reference — GoogleMap only fires panTo when the prop reference changes.
    setCenter({ lat: config.map.center.lat, lng: config.map.center.lng });
  }, [config.map.center, config.map.zoom]);

  const resetFilters = useCallback(() => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
  }, []);

  return {
    premiumHotelsList: hotelsQuery.data?.premiumHotelsList ?? [],
    freeHotelsList: hotelsQuery.data?.freeHotelsList ?? [],
    categories: categoriesQuery.data?.categories ?? [],
    customFilters: categoriesQuery.data?.customFilters,
    loading: hotelsQuery.isFetching,
    selectedCategory,
    selectedSubcategory,
    query,
    zoom,
    center,
    language,
    lat1,
    lat2,
    lon1,
    lon2,
    setQuery,
    setSelectedCategory,
    setSelectedSubcategory,
    setZoom,
    setCenter,
    setMapVisible,
    updateBounds,
    resetMap,
    resetFilters,
  };
};
